import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CosmicBackground from '../components/CosmicBackground';

const Pricing = () => {
  const [scrollY, setScrollY] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { scrollYProgress } = useScroll();
  const navigate = useNavigate();
  
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -100]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuyNow = (plan) => {
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    console.log('Closing payment modal...');
    setShowPaymentModal(false);
    setSelectedPlan(null);
  };

  const plans = [
    {
      name: "Astro Plan 1",
      price: "INR 500",
      duration: "session",
      color: "from-blue-500 to-blue-600",
      features: [
        "Consultation on 1 query",
        "10 min consultation time",
        "One Time Support",
        "Telephonic conversation",
        "In-depth Analysis",
        "Astro Recommendations"
      ]
    },
    {
      name: "Astro Plan 2",
      price: "INR 2000",
      duration: "session",
      color: "from-purple-500 to-purple-600",
      popular: true,
      features: [
        "One Area Analysis",
        "(Career/Finance/Marriage/Health; etc)",
        "25 min consultation time",
        "In-depth analysis",
        "Telephonic conversation",
        "Remedies & Guidance"
      ]
    },
    {
      name: "Astro Plan 3",
      price: "INR 2500",
      duration: "session",
      color: "from-indigo-500 to-indigo-600",
      features: [
        "Two Area Analysis",
        "35 min consultation time",
        "In-depth analysis",
        "Telephonic conversation",
        "Remedies & Guidance"
      ]
    },
    {
      name: "Astro Plan 4",
      price: "INR 3500",
      duration: "session",
      color: "from-pink-500 to-pink-600",
      features: [
        "Three Area Analysis",
        "45 min consultation time",
        "In-depth Analysis",
        "Telephonic conversation",
        "Remedies & Guidance"
      ]
    },
    {
      name: "Relationship Plan 1",
      price: "INR 3000",
      duration: "session",
      color: "from-red-500 to-red-600",
      features: [
        "Relationship Counselling",
        "Astro + Holistic Session",
        "45 Minutes Consultation Time",
        "Comprehensive Analysis",
        "Telephonic Conversation",
        "Remedies & Guidance"
      ]
    },
    {
      name: "Astro Plan 5",
      price: "INR 5100",
      duration: "session",
      color: "from-yellow-500 to-yellow-600",
      features: [
        "Full Birth Chart Analysis",
        "60 Minutes Consultation Time",
        "Comprehensive Analysis",
        "Telephonic Conversation",
        "Remedies & Guidance"
      ]
    },
    {
      name: "Relationship Plan 2",
      price: "INR 2000",
      duration: "session",
      color: "from-teal-500 to-teal-600",
      features: [
        "Matchmaking Analysis",
        "30 Minutes Consultation Time",
        "Comprehensive Analysis",
        "Telephonic Conversation",
        "Remedies & Guidance"
      ]
    }
  ];

  return (
    <CosmicBackground variant="pricing">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        className="min-h-screen relative z-10"
      >
        <div className="pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-center mb-12 sm:mb-16 md:mb-20"
            >
              <motion.h1
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-yellow-300 via-yellow-200 to-yellow-300 bg-clip-text text-transparent leading-tight py-2 px-4 sm:px-0"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                }}
                style={{
                  backgroundSize: "200% 200%",
                }}
              >
                ✨ Plans & Pricing ✨
              </motion.h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-200 max-w-4xl mx-auto leading-relaxed px-4 sm:px-0">
                Choose the perfect consultation plan that aligns with your spiritual journey and cosmic destiny
              </p>
            </motion.div>

            {/* Featured/Popular Plan */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-16"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-4">✨ Most Popular Choice ✨</h2>
                <p className="text-lg text-gray-300">Perfect for comprehensive astrology guidance</p>
              </div>
              
              {plans.filter(plan => plan.popular).map((plan, index) => (
                <motion.div
                  key={index}
                  whileHover={{ 
                    y: -10, 
                    scale: 1.02,
                    boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
                  }}
                  className="relative group bg-gradient-to-br from-white/15 to-white/10 backdrop-blur-xl rounded-2xl overflow-hidden border-2 border-yellow-400/50 shadow-2xl max-w-md mx-auto flex flex-col h-full"
                >
                  {/* Popular Badge */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-4 py-1.5 rounded-full text-xs font-bold z-10 shadow-lg"
                  >
                    <span className="hidden sm:inline">⭐ RECOMMENDED ⭐</span>
                    <span className="sm:hidden">⭐ TOP ⭐</span>
                  </motion.div>

                  {/* Header */}
                  <div className={`bg-gradient-to-r ${plan.color} p-6 text-white text-center relative overflow-hidden mt-3`}>
                    <h3 className="text-xl font-bold mb-3">{plan.name}</h3>
                    <div className="flex items-baseline justify-center mb-2">
                      <span className="text-3xl font-bold">{plan.price}</span>
                      <span className="text-sm ml-2 opacity-80">/{plan.duration}</span>
                    </div>
                    <p className="text-sm opacity-90">Complete Birth Chart Analysis</p>
                  </div>

                  {/* Features */}
                  <div className="p-6 flex flex-col flex-grow">
                    <ul className="space-y-3 mb-6 flex-grow">
                      {plan.features.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: featureIndex * 0.1, duration: 0.5 }}
                          className="flex items-start space-x-3"
                        >
                          <div className="w-5 h-5 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-gray-200 text-sm leading-relaxed">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>

                    <motion.button
                      onClick={() => handleBuyNow(plan)}
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: "0 0 25px rgba(234, 179, 8, 0.4)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-full bg-gradient-to-r ${plan.color} text-white py-3 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl mt-auto`}
                    >
                      ⭐ Choose This Plan
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Standard Plans Grid */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-16"
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4">🔮 Choose Your Consultation Plan</h2>
                <p className="text-lg text-gray-300">Professional astrology guidance tailored to your needs</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {plans.filter(plan => !plan.popular).slice(0, 6).map((plan, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 80 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    whileHover={{ 
                      y: -10, 
                      scale: 1.03,
                      boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                    }}
                    className="relative group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/20 hover:border-purple-400/50 transition-all duration-500 flex flex-col h-full"
                  >
                    {/* Header */}
                    <div className={`bg-gradient-to-r ${plan.color} p-6 text-white relative overflow-hidden`}>
                      <h3 className="text-xl font-bold mb-3">{plan.name}</h3>
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold">{plan.price}</span>
                        <span className="text-sm ml-2 opacity-80">/{plan.duration}</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="p-6 flex flex-col flex-grow">
                      <ul className="space-y-3 mb-8 flex-grow">
                        {plan.features.map((feature, featureIndex) => (
                          <li
                            key={featureIndex}
                            className="flex items-start space-x-3"
                          >
                            <div className="w-5 h-5 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className="text-gray-200 text-sm leading-relaxed">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <motion.button
                        onClick={() => handleBuyNow(plan)}
                        whileHover={{ 
                          scale: 1.05,
                          boxShadow: "0 0 25px rgba(168, 85, 247, 0.4)"
                        }}
                        whileTap={{ scale: 0.95 }}
                        className={`w-full bg-gradient-to-r ${plan.color} text-white py-3 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl mt-auto`}
                      >
                        📞 Book Now
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Specialty Plan */}
            {plans.filter(plan => !plan.popular).slice(6).map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mb-16"
              >
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4">💕 Specialty Relationship Plan</h2>
                  <p className="text-lg text-gray-300">Expert matchmaking and compatibility analysis</p>
                </div>
                
                <motion.div
                  whileHover={{ 
                    y: -8, 
                    scale: 1.02,
                    boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
                  }}
                  className="relative group bg-gradient-to-br from-white/12 to-white/8 backdrop-blur-xl rounded-3xl overflow-hidden border border-pink-400/30 shadow-xl max-w-xl mx-auto"
                >
                  {/* Header */}
                  <div className={`bg-gradient-to-r ${plan.color} p-10 text-white text-center relative overflow-hidden`}>
                    <h3 className="text-3xl font-bold mb-4">{plan.name}</h3>
                    <div className="flex items-baseline justify-center mb-4">
                      <span className="text-5xl font-bold">{plan.price}</span>
                      <span className="text-xl ml-3 opacity-80">/{plan.duration}</span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="p-10">
                    <ul className="space-y-4 mb-10">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-center space-x-4"
                        >
                          <div className="w-7 h-7 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-gray-200 text-lg leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <motion.button
                      onClick={() => handleBuyNow(plan)}
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: "0 0 30px rgba(236, 72, 153, 0.5)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-full bg-gradient-to-r ${plan.color} text-white py-5 rounded-2xl font-bold text-xl transition-all duration-300 shadow-xl`}
                    >
                      💕 Book Relationship Reading
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            ))}

            {/* Additional Info */}
            <motion.div
              initial={{ opacity: 0, y: 80 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="mt-32 text-center"
            >
              <div className="relative p-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl border border-purple-400/30 shadow-2xl">
                {/* Cosmic Glow Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-pink-600/10 rounded-3xl"
                  animate={{
                    boxShadow: [
                      "0 0 30px rgba(168, 85, 247, 0.3)",
                      "0 0 60px rgba(236, 72, 153, 0.4)",
                      "0 0 30px rgba(168, 85, 247, 0.3)",
                    ],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                />
                
                <h3 className="text-4xl font-bold mb-8 text-white">
                  Not Sure Which Plan to Choose?
                </h3>
                <p className="text-xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed">
                  Every plan includes personalized guidance, detailed analysis, and practical remedies tailored to your unique cosmic blueprint.
                </p>
                <div className="flex justify-center">
                  <motion.button
                    onClick={() => navigate('/contact')}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 0 35px rgba(139, 69, 19, 0.6)",
                      rotateY: 5,
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 text-white px-12 py-6 rounded-2xl font-bold text-xl shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 relative overflow-hidden border-2 border-amber-400/30"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-amber-500 via-orange-400 to-amber-500"
                      animate={{
                        x: ["-100%", "100%"],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        repeatType: "reverse",
                      }}
                    />
                    
                    {/* Floating Contact Icons */}
                    <motion.div
                      className="absolute -top-2 -left-2 w-6 h-6 bg-white/20 rounded-full"
                      animate={{
                        y: [0, -8, 0],
                        rotate: [0, 180, 360],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                      }}
                    />
                    <motion.div
                      className="absolute -bottom-2 -right-2 w-4 h-4 bg-white/30 rounded-full"
                      animate={{
                        y: [0, 8, 0],
                        rotate: [360, 180, 0],
                      }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        delay: 0.5,
                      }}
                    />
                    
                    <span className="relative z-10 flex items-center gap-3">
                      <motion.span
                        animate={{
                          rotate: [0, 15, -15, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                        }}
                      >
                        📞
                      </motion.span>
                      Contact Us
                      <motion.span
                        animate={{
                          scale: [1, 1.2, 1],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                        }}
                      >
                        ✨
                      </motion.span>
                    </span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <div
            className="fixed inset-0 z-[9999] flex items-start justify-center p-6 bg-black/80 backdrop-blur-lg pt-20 pb-6"
            onClick={closePaymentModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-sm bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/20 p-6 shadow-2xl max-h-[calc(100vh-7rem)] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button - Fully visible inside modal */}
              <button
                onClick={(e) => {
                  console.log('Close button clicked - simplified version!');
                  e.preventDefault();
                  e.stopPropagation();
                  setShowPaymentModal(false);
                  setSelectedPlan(null);
                }}
                className="absolute top-3 right-3 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-xl transition-all duration-300 z-[99999] border-2 border-white cursor-pointer"
                style={{ 
                  minWidth: '40px', 
                  minHeight: '40px',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              >
                ✕
              </button>

              {/* Modal Header */}
              <div className="text-center mb-6 mt-4 px-4">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-2xl font-bold text-white mb-3"
                >
                  Complete Your Purchase
                </motion.h2>
                {selectedPlan && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${selectedPlan.color} text-white font-semibold shadow-lg text-sm`}
                  >
                    {selectedPlan.name} - {selectedPlan.price}
                  </motion.div>
                )}
              </div>

              {/* QR Code Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center mb-4"
              >
                <div className="relative inline-block p-4 bg-white rounded-2xl shadow-2xl">
                  <img
                    src="/astro-payment.jpg"
                    alt="UPI QR Code"
                    className="w-56 h-56 object-contain"
                    onError={(e) => {
                      // If image fails to load, show fallback
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div 
                    className="w-56 h-56 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex flex-col items-center justify-center text-gray-700 border-2 border-dashed border-gray-400"
                    style={{ display: 'none' }}
                  >
                    <div className="text-5xl mb-3">📱</div>
                    <div className="text-center">
                      <p className="font-bold text-lg mb-2">UPI QR Code</p>
                      <p className="text-sm text-gray-600">Scan with Google Pay</p>
                      <p className="text-sm text-gray-600 mt-1">or any UPI app</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Payment Instructions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center"
              >
                <p className="text-base text-gray-200 leading-relaxed mb-4">
                  We accept payment through Google Pay. Please complete your purchase by sending amount on above UPI ID or scan the QR code
                </p>
                
                {/* Contact Information */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-4 p-3 bg-purple-500/20 rounded-xl border border-purple-400/30"
                >
                  <p className="text-yellow-300 font-semibold">
                    For further enquiries, call us at: 
                    <a 
                      href="tel:+919586074349" 
                      className="hover:text-yellow-200 transition-colors duration-300 ml-1"
                    >
                      +91-9586074349
                    </a>
                  </p>
                </motion.div>
              </motion.div>

              {/* Floating Cosmic Particles */}
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-yellow-300 rounded-full opacity-40"
                  style={{
                    left: `${10 + i * 20}%`,
                    top: `${15 + (i % 2) * 40}%`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.2, 0.8, 0.2],
                    scale: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 3 + i * 0.5,
                    repeat: Infinity,
                    delay: i * 0.3,
                  }}
                />
              ))}
            </motion.div>
          </div>
        )}
      </motion.div>
    </CosmicBackground>
  );
};

export default Pricing;
